import { MeetingInfo, EmailDraft } from '../models/types';
import { formatDate, formatEmailDate } from '../utils/dateFormatter';
import { 
  generateParticipantListHtml, 
  generateActivitiesHtml,
  generateParticipantListText,
  generateActivitiesText,
  generateFraportEmailTemplate
} from '../utils/templateHelpers';
import { v4 as uuidv4 } from 'uuid';

/**
 * Service for generating email templates based on meeting information
 */
export class EmailTemplateService {
  /**
   * Generates an email draft based on meeting information
   * @param meetingInfo The meeting information
   * @param userId The ID of the user generating the draft
   * @returns An EmailDraft object
   */
  public generateEmailDraft(meetingInfo: MeetingInfo, userId: string): EmailDraft {
    const subject = this.generateSubject(meetingInfo);
    const htmlContent = this.createHtmlTemplate(meetingInfo);
    const textContent = this.createTextTemplate(meetingInfo);
    
    return {
      id: uuidv4(),
      subject,
      htmlContent,
      textContent,
      meetingInfo,
      createdAt: new Date(),
      createdBy: userId
    };
  }
  
  /**
   * Generates the email subject
   * @param meetingInfo The meeting information
   * @returns The email subject
   */
  private generateSubject(meetingInfo: MeetingInfo): string {
    return `Invitation: ${meetingInfo.title} - ${formatDate(meetingInfo.meetingDate, 'dd.MM.yyyy')}`;
  }
  
  /**
   * Creates an HTML email template
   * @param meetingInfo The meeting information
   * @returns HTML content
   */
  private createHtmlTemplate(meetingInfo: MeetingInfo): string {
    const content = `
      <p>Dear colleagues,</p>
      <p>You are invited to the following meeting:</p>
      
      <h2>Meeting Details</h2>
      <p><strong>Title:</strong> ${meetingInfo.title}</p>
      <p><strong>Date:</strong> ${formatEmailDate(meetingInfo.meetingDate)}</p>
      ${meetingInfo.endDate ? `<p><strong>End Date:</strong> ${formatEmailDate(meetingInfo.endDate)}</p>` : ''}
      ${meetingInfo.description ? `<p><strong>Description:</strong> ${meetingInfo.description}</p>` : ''}
      ${meetingInfo.comments ? `<p><strong>Comments:</strong> ${meetingInfo.comments}</p>` : ''}
      
      <h2>Activities and Deadlines</h2>
      <table>
        <tr>
          <th>Activity</th>
          <th>Start Date</th>
          <th>End Date</th>
          <th>Description</th>
        </tr>
        ${generateActivitiesHtml(meetingInfo.activities)}
      </table>
      
      <h2>Participants</h2>
      <ul>
        ${generateParticipantListHtml(meetingInfo.participants)}
      </ul>
      
      <p>Please review the attached information and prepare accordingly.</p>
      <p>Best regards,<br>Fraport Team</p>
    `;
    
    return generateFraportEmailTemplate(meetingInfo.title, content);
  }
  
  /**
   * Creates a plain text email template
   * @param meetingInfo The meeting information
   * @returns Plain text content
   */
  private createTextTemplate(meetingInfo: MeetingInfo): string {
    return `
INVITATION: ${meetingInfo.title}

Date: ${formatEmailDate(meetingInfo.meetingDate)}
${meetingInfo.endDate ? `End Date: ${formatEmailDate(meetingInfo.endDate)}\n` : ''}
${meetingInfo.description ? `Description: ${meetingInfo.description}\n` : ''}
${meetingInfo.comments ? `Comments: ${meetingInfo.comments}\n` : ''}

ACTIVITIES AND DEADLINES:
${generateActivitiesText(meetingInfo.activities)}

PARTICIPANTS:
${generateParticipantListText(meetingInfo.participants)}

Please review the attached information and prepare accordingly.

Best regards,
Fraport Team

---
This is an automatically generated email. Please do not reply directly to this message.
    `;
  }
}